import { storage, type EnterpriseRoleEntry, type FareAuthUser } from "./storage";

export type { FareAuthUser, EnterpriseRoleEntry };

export const AUTH_KEYS = {
  token: "authToken",
  refreshToken: "refreshToken",
  userId: "userId",
  username: "username",
  userName: "userName",
  userEmail: "userEmail",
  userData: "userData",
  mobile: "mobileLoginNumber",
  registrationOtp: "registrationOtp",
  loginOtpEmail: "loginOtpEmail",
  loginOtpMobile: "loginOtpMobile",
  loginOtpCode: "loginOtpCode",
  loginOtpUserId: "loginOtpUserId",
} as const;

function canUseStorage() {
  return typeof window !== "undefined";
}

export function getAuthToken(): string {
  return storage.getToken();
}

export function getRefreshToken(): string {
  return storage.getRefreshToken();
}

export function isAuthenticated(): boolean {
  return Boolean(getAuthToken());
}

export function getSessionUser(): FareAuthUser | null {
  return storage.getUser<FareAuthUser>();
}

export function getSessionUserId(): string {
  const user = getSessionUser();
  if (user?.userId) return user.userId;
  const token = getAuthToken();
  return token ? decodeJwtUserId(token) || "" : "";
}

export function getSessionUserName(): string {
  const user = getSessionUser();
  return user?.userName || user?.username || "";
}

export function saveLoginSession(params: {
  token: string;
  refreshToken?: string;
  userId?: string;
  username: string;
  userData?: unknown;
  userName?: string;
  userEmail?: string;
  enterpriseRoles?: EnterpriseRoleEntry[];
  selectedEnterpriseId?: string;
}) {
  storage.setTokens(params.token, params.refreshToken);
  const existing = storage.getUser<FareAuthUser>();
  const next: FareAuthUser = {
    userId: params.userId || existing?.userId || decodeJwtUserId(params.token) || "",
    username: params.username || existing?.username || "",
    userName: params.userName || existing?.userName || params.username,
    userEmail: params.userEmail || existing?.userEmail || "",
    userData: (params.userData as Record<string, unknown> | undefined) || existing?.userData,
    enterpriseRoles: params.enterpriseRoles || existing?.enterpriseRoles,
    selectedEnterpriseId: params.selectedEnterpriseId || existing?.selectedEnterpriseId,
  };
  storage.setUser(next);
}

export function saveSignupDraft(params: {
  mobile: string;
  name?: string;
  email?: string;
  otp?: string;
  userId?: string;
}) {
  if (!canUseStorage()) return;
  sessionStorage.setItem(AUTH_KEYS.mobile, params.mobile);
  if (params.name) sessionStorage.setItem(AUTH_KEYS.userName, params.name);
  if (params.email) sessionStorage.setItem(AUTH_KEYS.userEmail, params.email);
  if (params.otp) sessionStorage.setItem(AUTH_KEYS.registrationOtp, params.otp);
  if (params.userId) sessionStorage.setItem(AUTH_KEYS.userId, params.userId);
}

export function getSignupDraft() {
  if (!canUseStorage()) {
    return { mobile: "", name: "", email: "", otp: "", userId: "" };
  }
  return {
    mobile: sessionStorage.getItem(AUTH_KEYS.mobile) || "",
    name: sessionStorage.getItem(AUTH_KEYS.userName) || "",
    email: sessionStorage.getItem(AUTH_KEYS.userEmail) || "",
    otp: sessionStorage.getItem(AUTH_KEYS.registrationOtp) || "",
    userId: sessionStorage.getItem(AUTH_KEYS.userId) || "",
  };
}

export function clearSignupDraft() {
  if (!canUseStorage()) return;
  sessionStorage.removeItem(AUTH_KEYS.mobile);
  sessionStorage.removeItem(AUTH_KEYS.registrationOtp);
  sessionStorage.removeItem(AUTH_KEYS.userId);
  sessionStorage.removeItem(AUTH_KEYS.userName);
  sessionStorage.removeItem(AUTH_KEYS.userEmail);
}

// Passwordless first-login (invited user) flow — kept separate from the
// signup draft above since it's a distinct flow with its own endpoints
// (see lib/auth/api.ts invitedAuthApi).
export function saveLoginOtpDraft(params: { email: string; mobile: string; otp?: string; userId?: string }) {
  if (!canUseStorage()) return;
  sessionStorage.setItem(AUTH_KEYS.loginOtpEmail, params.email);
  sessionStorage.setItem(AUTH_KEYS.loginOtpMobile, params.mobile);
  if (params.otp) sessionStorage.setItem(AUTH_KEYS.loginOtpCode, params.otp);
  if (params.userId) sessionStorage.setItem(AUTH_KEYS.loginOtpUserId, params.userId);
}

export function getLoginOtpDraft() {
  if (!canUseStorage()) {
    return { email: "", mobile: "", otp: "", userId: "" };
  }
  return {
    email: sessionStorage.getItem(AUTH_KEYS.loginOtpEmail) || "",
    mobile: sessionStorage.getItem(AUTH_KEYS.loginOtpMobile) || "",
    otp: sessionStorage.getItem(AUTH_KEYS.loginOtpCode) || "",
    userId: sessionStorage.getItem(AUTH_KEYS.loginOtpUserId) || "",
  };
}

export function clearLoginOtpDraft() {
  if (!canUseStorage()) return;
  sessionStorage.removeItem(AUTH_KEYS.loginOtpEmail);
  sessionStorage.removeItem(AUTH_KEYS.loginOtpMobile);
  sessionStorage.removeItem(AUTH_KEYS.loginOtpCode);
  sessionStorage.removeItem(AUTH_KEYS.loginOtpUserId);
}

export function clearAuthSession() {
  storage.clear();
}

export function decodeJwtUserId(token: string): string | null {
  try {
    const payload = token.split(".")[1];
    if (!payload) return null;
    const normalized = payload.replace(/-/g, "+").replace(/_/g, "/");
    const padded = normalized.padEnd(Math.ceil(normalized.length / 4) * 4, "=");
    const json = JSON.parse(atob(padded)) as Record<string, unknown>;
    const id = json.userId || json.user_id || json.id || json.sub || null;
    return id == null ? null : String(id);
  } catch {
    return null;
  }
}
